import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import get from 'lodash/get';
import noop from 'lodash/noop';

import { useAccessToken, useSnackBar } from '@sbv-clients/ui-shared';
import { UnwrapArray } from '@sbv-clients/util-misc';

import {
  Api,
  CreateOpeningTemplate,
  OpeningTemplateApiOpeningTemplateGetRequest,
  OpeningTemplateApiOpeningTemplateOpeningtemplatesByTypeAndPositionGetRequest,
  OpeningTemplateApiOpeningTemplateBaseSchedulesGetRequest,
  OpeningTemplateCacheKeys,
  useApi,
  OpeningTemplateApiOpeningTemplateOpeningTemplateIdPutRequest,
  ViewOpeningTemplateDto,
  OpeningTemplateApiOpeningTemplateBulkUpdateOpeningTemplatesPostRequest,
} from '../index';

export const useOpeningTemplatesByTypeAndPositionGet = (
  body: OpeningTemplateApiOpeningTemplateOpeningtemplatesByTypeAndPositionGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [OpeningTemplateCacheKeys.OpeningTemplate, body],
    queryFn: () =>
      api.openingTemplateApi.openingTemplateOpeningtemplatesByTypeAndPositionGet(
        body
      ),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
export const useGetAvailableEmployeesForMasterView = (
  openingTemplateId: number,
  weekNumber?: number,
  dayOfWeek?: number,
  searchQuery?: string,
  masterScheduleDraftId?: number
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      OpeningTemplateCacheKeys.AvailableEmployees,
      searchQuery,
      openingTemplateId,
      weekNumber,
      dayOfWeek,
      masterScheduleDraftId,
    ],
    queryFn: () =>
      api.masterScheduleApi
        .masterScheduleAvailableEmployeesOpeningTemplateIdGet({
          searchParam: searchQuery,
          openingTemplateId,
          weekNumber,
          dayOfWeek,
          masterScheduleDraftId,
        })
        .then((response) => response.data.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(openingTemplateId),
  });
};
type AvailableEmployeesForMasterViewResponseType = NonNullable<
  ReturnType<typeof useGetAvailableEmployeesForMasterView>['data']
>;
export type MasterViewEmployeeType = NonNullable<
  UnwrapArray<AvailableEmployeesForMasterViewResponseType>
>;

export const useOpeningTemplateGet = (
  body: OpeningTemplateApiOpeningTemplateGetRequest
) => {
  const { isMultiFacility } = useAccessToken();
  const api = useApi();
  return useQuery({
    queryKey: [OpeningTemplateCacheKeys.OpeningTemplate, body],
    queryFn: () =>
      api.openingTemplateApi
        .openingTemplateGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
export type OpeningTemplateResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useOpeningTemplateGet>['data']>
>;
export const useOpeningTemplateIdGet = (openingTemplateId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [OpeningTemplateCacheKeys.OpeningTemplate, { openingTemplateId }],
    queryFn: () =>
      api.openingTemplateApi
        .openingTemplateOpeningTemplateIdGet({
          openingTemplateId,
        })
        .then((response) => response.data.data),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
export type OpeningTemplateItemResponseType = NonNullable<
  ReturnType<typeof useOpeningTemplateIdGet>['data']
>;
export const useOpeningTemplateIdGetWithMutation = (
  openingTemplateId: number
) => {
  const api = useApi();
  return useMutation(() => {
    return api.openingTemplateApi
      .openingTemplateOpeningTemplateIdGet({
        openingTemplateId,
      })
      .then((response) => response.data.data ?? {});
  });
};
const createOpeningTemplate =
  (api: Api) => (createOpeningTemplate: CreateOpeningTemplate) =>
    api.openingTemplateApi.openingTemplatePost({
      createOpeningTemplate,
    });
export const useCreateOpeningTemplate = () => {
  const api = useApi();
  return useMutation(createOpeningTemplate(api));
};

const updateOpeningTemplate =
  (api: Api) =>
  (body: OpeningTemplateApiOpeningTemplateOpeningTemplateIdPutRequest) => {
    return api.openingTemplateApi.openingTemplateOpeningTemplateIdPut(body);
  };
export const useUpdateOpeningTemplate = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(updateOpeningTemplate(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([OpeningTemplateCacheKeys.OpeningTemplate])
        .catch(noop);
    },
    onError: async (error) => {
      const message =
        get(error, 'response.data.message', '') || get(error, 'message', '');
      notifyError(message);
    },
  });
};

const bulkUpdateOpeningTemplate =
  (api: Api) =>
  (
    body: OpeningTemplateApiOpeningTemplateBulkUpdateOpeningTemplatesPostRequest
  ) => {
    return api.openingTemplateApi.openingTemplateBulkUpdateOpeningTemplatesPost(
      body
    );
  };
export const useBulkUpdateOpeningTemplate = () => {
  const api = useApi();
  return useMutation(bulkUpdateOpeningTemplate(api));
};

export const useOpeningTemplateBaseSchedulesGet = (
  body: OpeningTemplateApiOpeningTemplateBaseSchedulesGetRequest
) => {
  const api = useApi();
  const { isMultiFacility } = useAccessToken();
  return useQuery({
    queryKey: ['base-schedules', body],
    queryFn: () =>
      api.openingTemplateApi
        .openingTemplateBaseSchedulesGet(body)
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: (isMultiFacility && Boolean(body.facilityId)) || !isMultiFacility,
  });
};
export type BaseSchedulesResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useOpeningTemplateBaseSchedulesGet>['data']>
>;
